import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useCompany, useUpdateCompany } from "@/hooks";
import { UtilsService } from "@/services/utils/utils.service";
import { Button } from "@/components/ui/button";
import { StyledInput } from "@/components/ui/styled-input";
import { StyledLabel } from "@/components/ui/styled-label";
import { StyledTextarea } from "@/components/ui/styled-textarea";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { Loader2 } from "lucide-react";

const utils = new UtilsService();

const companySchema = z.object({
  name: z.string().min(1, "Company name is required").min(2, "Company name must be at least 2 characters"),
  address: z.string().optional(),
  website: z.string().url("Please enter a valid website URL").optional().or(z.literal("")),
  phone: z.string().optional(),
});

type CompanyFormData = z.infer<typeof companySchema>;

const CompanyDetailsForm = () => {
  const { t } = useTranslation();
  const { data: company, isLoading } = useCompany();
  const updateCompanyMutation = useUpdateCompany();

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset,
  } = useForm<CompanyFormData>({
    resolver: zodResolver(companySchema),
    defaultValues: {
      name: "",
      address: "",
      website: "",
      phone: "",
    },
  });

  const phoneValue = watch("phone");

  useEffect(() => {
    if (company) {
      reset({
        name: company.name || "",
        address: company.address || "",
        website: company.website || "",
        phone: company.phone || "",
      });
    }
  }, [company, reset]);

  const onSubmit = async (data: CompanyFormData) => {
    try {
      if (!company?.id) {
        toast.error(t("company.errors.noCompany"));
        return;
      }

      await updateCompanyMutation.mutateAsync({
        companyId: company.id,
        data: {
          name: data.name,
          address: data.address || undefined,
          website: data.website || undefined,
          phone: data.phone || undefined,
        },
      });

      toast.success(t("company.details.updated"));
    } catch (error: unknown) {
      const errorMessage = utils.extractErrorMessage(
        error,
        t("company.details.updateFailed")
      );
      toast.error(errorMessage);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (!company) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">{t("company.errors.noCompany")}</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <div>
        <StyledLabel htmlFor="name" required>
          {t("onboarding.steps.company.name")}
        </StyledLabel>
        <StyledInput
          id="name"
          type="text"
          placeholder={t("onboarding.steps.company.name")}
          error={errors.name?.message}
          {...register("name")}
        />
      </div>

      <div>
        <StyledLabel htmlFor="address">
          {t("onboarding.steps.company.address")}
        </StyledLabel>
        <StyledTextarea
          id="address"
          {...register("address")}
          rows={3}
          placeholder={t("onboarding.steps.company.address")}
          error={errors.address?.message}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <StyledLabel htmlFor="website">
            {t("onboarding.steps.company.website")}
          </StyledLabel>
          <StyledInput
            id="website"
            type="url"
            placeholder="https://example.com"
            error={errors.website?.message}
            {...register("website")}
          />
        </div>

        <div>
          <StyledLabel htmlFor="phone">
            {t("onboarding.steps.company.phone")}
          </StyledLabel>
          <div className="mt-1">
            <PhoneInput
              country={'rw'}
              value={phoneValue || ""}
              onChange={(value) => {
                setValue("phone", value, { shouldValidate: true });
              }}
              inputProps={{
                id: "phone",
              }}
              containerClass={errors.phone ? "phone-input-error" : ""}
              inputClass={errors.phone ? "border-red-500" : ""}
            />
          </div>
          {errors.phone && (
            <p className="text-red-500 text-sm mt-1 pl-2">{errors.phone.message}</p>
          )}
        </div>
      </div>

      <div className="flex justify-end mt-6">
        <Button
          type="submit"
          disabled={updateCompanyMutation.isPending}
        >
          {updateCompanyMutation.isPending ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {t("common.saving")}
            </>
          ) : (
            t("company.details.save")
          )}
        </Button>
      </div>
    </form>
  );
};

export default CompanyDetailsForm;

